const ACTIVE_STATES = ['queued', 'creating_archive']

const DATE_FORMAT = 'DD/MM/YYYY HH:mm'
const MISSING_VALUE = '—'
const PAGE_SIZE = 10
const DOWNLOADS_MODAL_ID = 'downloads-modal'
const POLL_INTERVAL_MS = 5000
const POLL_MAX_FAILURES = 3
const CANCEL_PENDING_MAX_MS = 60000
const DOWNLOAD_REFETCH_DELAY_MS = 3000
const AUTO_REFRESH_SUSPENDED_TEXT = 'Aggiornamento automatico sospeso. Usa Aggiorna per riprovare.'

const MODAL_INTRO_TEXT = 'Qui trovi le tue richieste di download massivo. Gli archivi pronti restano disponibili per 7 giorni.'
const EMPTY_TITLE = 'Nessuna richiesta di download'
const EMPTY_TEXT = 'Quando avvii un download da una cartella o da più file (menu contestuale, voce Download), la richiesta compare qui con il suo stato.'

const ADD_ERROR_OVERRIDES = {
  422: 'Hai raggiunto il numero massimo di download in preparazione. Attendi che uno termini o annullalo da I miei download.',
  429: 'Troppe richieste di download. Riprova tra un minuto.',
  default: 'Download non avviato!',
}

const CANCEL_CONFIRM = {
  title: 'Annulla download',
  body: "Annullare la preparazione dell'archivio? Dovrai richiederlo di nuovo.",
  okTitle: 'Sì, annulla download',
  cancelTitle: 'Mantieni download',
}

const CANCEL_ERROR_OVERRIDES = {
  403: 'Download non trovato o non tuo.',
  404: 'Download non trovato o non tuo.',
  422: 'Questo download non può più essere annullato.',
}

const DOWNLOAD_ERROR_OVERRIDES = {
  403: 'Download non trovato o non tuo.',
  404: 'Download non trovato o non tuo.',
  422: 'Questo archivio non è più scaricabile. Richiedi di nuovo il download.',
  default: 'Download non avviato. Riprova.',
}

const QUEUED_TOAST_TITLE = 'In coda'
const QUEUED_TOAST_TEXT = "Richiesta in coda: l'archivio viene preparato in background. Riceverai un'email quando sarà pronto, oppure segui lo stato in I miei download."
const QUEUED_TOAST_ACTION = 'Vedi i miei download'
const QUEUED_TOAST_TIMEOUT_MS = 8000

const STATE_VIEWS = {
  queued: {
    label: 'In coda', variant: 'light-info', spinner: true, note: null,
  },
  creating_archive: {
    label: 'In preparazione', variant: 'light-warning', spinner: true, note: null,
  },
  ready_for_download: {
    label: 'Pronto', variant: 'light-success', spinner: false, note: null,
  },
  pulled_from_storage: {
    label: 'Scaricato', variant: 'light-secondary', spinner: false, note: null,
  },
  error: {
    label: 'Errore', variant: 'light-danger', spinner: false, note: 'Richiedi di nuovo il download dai file',
  },
  cancelled: {
    label: 'Annullato', variant: 'light-secondary', spinner: false, note: null,
  },
  archive_expired: {
    label: 'Scaduto', variant: 'light-dark', spinner: false, note: 'Archivio rimosso dopo 7 giorni: richiedilo di nuovo',
  },
}

const UNKNOWN_STATE_VIEW = {
  label: 'Sconosciuto', variant: 'light-secondary', spinner: false, note: null,
}

const CANCELLING_STATE_VIEW = {
  label: 'Annullamento in corso', variant: 'light-warning', spinner: true, note: null,
}

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key)

const isActive = row => (
  typeof row === 'object' && row !== null
  && typeof row.process_state === 'string'
  && ACTIVE_STATES.includes(row.process_state)
)

const isCancelling = row => isActive(row) && Boolean(row.cancel_requested_at)

const getStateView = row => {
  if (isCancelling(row)) {
    return { ...CANCELLING_STATE_VIEW, canCancel: false, canDownload: false }
  }
  const state = row && typeof row.process_state === 'string' ? row.process_state : null
  const view = state !== null && hasOwn(STATE_VIEWS, state) ? STATE_VIEWS[state] : UNKNOWN_STATE_VIEW
  return {
    ...view,
    canCancel: isActive(row) && !row.cancel_requested_at,
    canDownload: state === 'ready_for_download',
  }
}

const hasActive = rows => Array.isArray(rows) && rows.some(isActive)

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB']

const formatBytes = bytes => {
  const n = Number(bytes)
  if (!Number.isFinite(n) || n <= 0) {
    return MISSING_VALUE
  }
  let value = n
  let i = 0
  while (value >= 1024 && i < BYTE_UNITS.length - 1) {
    value /= 1024
    i += 1
  }
  const unit = BYTE_UNITS[i]
  if (i === 0) {
    return `${Math.round(value)} ${unit}`
  }
  const rounded = Math.round(value * 10) / 10
  const text = rounded < 10 ? rounded.toFixed(1).replace('.', ',') : String(Math.round(value))
  return `${text} ${unit}`
}

const timestampOf = row => Date.parse(String(row ? row.created_at : undefined))

const idOf = row => {
  const id = Number(row ? row.id : undefined)
  return Number.isNaN(id) ? 0 : id
}

const sortRows = rows => {
  if (!Array.isArray(rows)) {
    return []
  }
  return [...rows].sort((a, b) => {
    const ta = timestampOf(a)
    const tb = timestampOf(b)
    const aDated = !Number.isNaN(ta)
    const bDated = !Number.isNaN(tb)
    if (aDated !== bDated) {
      return aDated ? -1 : 1
    }
    if (aDated && ta !== tb) {
      return tb - ta
    }
    return idOf(b) - idOf(a)
  })
}

const markCancelRequested = (rows, id, nowIso = new Date().toISOString()) => {
  if (!Array.isArray(rows)) {
    return []
  }
  return rows.map(row => (
    row && row.id === id
      ? { ...row, cancel_requested_at: row.cancel_requested_at || nowIso }
      : row
  ))
}

const readListRows = response => {
  if (response && response.data && Number(response.data.code) === 1) {
    return Array.isArray(response.data.data) ? [...response.data.data] : []
  }
  return null
}

const readDownloadLink = response => {
  if (!(response && response.data && Number(response.data.code) === 1)) {
    return null
  }
  const payload = response.data.data
  const link = payload && typeof payload === 'object' ? payload.link : undefined
  if (typeof link !== 'string') {
    return null
  }
  try {
    const { protocol } = new URL(link)
    return protocol === 'https:' || protocol === 'http:' ? link : null
  } catch (error) {
    return null
  }
}

const startNativeDownload = (url, frame = null, doc = document) => {
  const target = frame || doc.createElement('iframe')
  if (!frame) {
    target.hidden = true
    target.tabIndex = -1
    target.title = 'Download archivio'
    target.setAttribute('aria-hidden', 'true')
  }
  if (!target.parentNode) {
    doc.body.appendChild(target)
  }
  target.src = url
  return target
}

const createDownloadRefetch = ({
  onSettle,
  delayMs = DOWNLOAD_REFETCH_DELAY_MS,
  setTimeoutFn = (fn, ms) => setTimeout(fn, ms),
  clearTimeoutFn = handle => clearTimeout(handle),
}) => {
  const timers = new Map()

  const schedule = id => {
    if (timers.has(id)) {
      clearTimeoutFn(timers.get(id))
      timers.delete(id)
    }
    let handle
    const fire = () => {
      // A cleared or re-scheduled timer that still fires is a no-op.
      if (!timers.has(id) || timers.get(id) !== handle) {
        return undefined
      }
      timers.delete(id)
      // A failing refetch must never surface as an unhandled rejection (load() reports its own errors).
      return Promise.resolve().then(() => onSettle(id)).catch(() => {})
    }
    handle = setTimeoutFn(fire, delayMs)
    timers.set(id, handle)
  }

  const cancelAll = () => {
    const ids = [...timers.keys()]
    timers.forEach(handle => clearTimeoutFn(handle))
    timers.clear()
    return ids
  }

  return { schedule, cancelAll, pendingIds: () => [...timers.keys()] }
}

const mergeList = (serverRows, pendingCancelIds, nowIso = new Date().toISOString()) => {
  if (!Array.isArray(serverRows)) {
    return { rows: [], pendingCancelIds: [] }
  }
  const pending = Array.isArray(pendingCancelIds) ? pendingCancelIds : []
  const stillPending = []
  const rows = serverRows.map(row => {
    if (!isActive(row) || row.cancel_requested_at || !pending.includes(row.id)) {
      return row
    }
    stillPending.push(row.id)
    return { ...row, cancel_requested_at: nowIso }
  })
  return { rows, pendingCancelIds: stillPending }
}

const expirePendingCancels = (pendingCancelIds, stampedAt, nowMs = Date.now(), maxMs = CANCEL_PENDING_MAX_MS) => {
  if (!Array.isArray(pendingCancelIds)) {
    return []
  }
  const stamps = stampedAt && typeof stampedAt === 'object' ? stampedAt : {}
  return pendingCancelIds.filter(id => (
    hasOwn(stamps, id) && Number.isFinite(stamps[id]) && nowMs - stamps[id] < maxMs
  ))
}

const createPoller = ({
  fetchRows,
  onRows,
  onError,
  intervalMs = POLL_INTERVAL_MS,
  maxFailures = POLL_MAX_FAILURES,
  setTimeoutFn = (fn, ms) => setTimeout(fn, ms),
  clearTimeoutFn = id => clearTimeout(id),
}) => {
  let timer = null
  let running = false
  let generation = 0
  let failures = 0
  let tick = null

  const schedule = () => {
    timer = setTimeoutFn(tick, intervalMs)
  }

  const isStale = tickGeneration => !running || tickGeneration !== generation

  const reportError = (error, info) => {
    try {
      onError(error, info)
    } catch (callbackError) {
      // A throwing error callback must never surface as an unhandled rejection.
    }
  }

  tick = async () => {
    timer = null
    const tickGeneration = generation
    let result
    try {
      result = await fetchRows()
    } catch (error) {
      if (!isStale(tickGeneration)) {
        failures += 1
        const unauthorized = Boolean(error && error.response && error.response.status === 401)
        const suspended = unauthorized || failures >= maxFailures
        if (suspended) {
          running = false
        } else {
          schedule()
        }
        reportError(error, { failures, suspended, unauthorized })
      }
      return
    }
    if (isStale(tickGeneration)) {
      return
    }
    failures = 0
    const rows = Array.isArray(result) ? result : []
    try {
      onRows(rows)
    } catch (callbackError) {
      running = false
      reportError(callbackError, { failures: maxFailures, suspended: true, unauthorized: false })
      return
    }
    if (hasActive(rows)) {
      schedule()
    } else {
      running = false
    }
  }

  const ensureRunning = () => {
    if (running) {
      return
    }
    running = true
    failures = 0
    generation += 1
    schedule()
  }

  const stop = () => {
    running = false
    generation += 1
    if (timer !== null) {
      clearTimeoutFn(timer)
      timer = null
    }
  }

  return { ensureRunning, stop, isRunning: () => running }
}

export default {
  ACTIVE_STATES,
  STATE_VIEWS,
  UNKNOWN_STATE_VIEW,
  CANCELLING_STATE_VIEW,
  isActive,
  isCancelling,
  getStateView,
  hasActive,
  formatBytes,
  sortRows,
  readListRows,
  markCancelRequested,
  mergeList,
  expirePendingCancels,
  createPoller,
  readDownloadLink,
  startNativeDownload,
  createDownloadRefetch,
  POLL_INTERVAL_MS,
  POLL_MAX_FAILURES,
  CANCEL_PENDING_MAX_MS,
  DOWNLOAD_REFETCH_DELAY_MS,
  AUTO_REFRESH_SUSPENDED_TEXT,
  DATE_FORMAT,
  MISSING_VALUE,
  PAGE_SIZE,
  DOWNLOADS_MODAL_ID,
  MODAL_INTRO_TEXT,
  EMPTY_TITLE,
  EMPTY_TEXT,
  ADD_ERROR_OVERRIDES,
  CANCEL_CONFIRM,
  CANCEL_ERROR_OVERRIDES,
  DOWNLOAD_ERROR_OVERRIDES,
  QUEUED_TOAST_TITLE,
  QUEUED_TOAST_TEXT,
  QUEUED_TOAST_ACTION,
  QUEUED_TOAST_TIMEOUT_MS,
}
